import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Info } from "lucide-react";
import OperatorProfile from "@/components/admin/OperatorProfile";
import ServiceManagement from "@/components/admin/ServiceManagement";
import OperatorDashboard from "@/components/admin/OperatorDashboard";
import PhotoGallery from "@/components/admin/PhotoGallery";
import TagsCategories from "@/components/admin/TagsCategories";
import OperatorAnalytics from "@/components/admin/OperatorAnalytics";
import PlanDisplay from "@/components/admin/PlanDisplay";
import SupportMessages from "@/components/admin/SupportMessages";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface Operator {
  id: string;
  email: string;
  name: string;
  isPremium: boolean;
  isBasic?: boolean;
}

const OperatorAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [operator, setOperator] = useState<Operator | null>(null);
  
  useEffect(() => {
    // Check if user is logged in
    const storedOperator = localStorage.getItem("operator");
    
    if (!storedOperator) {
      navigate("/login");
      return;
    }
    
    try {
      setOperator(JSON.parse(storedOperator));
    } catch (error) {
      localStorage.removeItem("operator");
      navigate("/login");
    }
  }, [navigate]);

  // Mock stats data for the insights table
  const statsData = [
    { metric: "Profile Views", value: 243, change: "+12%", trend: "up" },
    { metric: "Contact Clicks", value: 36, change: "+8%", trend: "up" },
    { metric: "Booking Requests", value: 15, change: "-3%", trend: "down" },
    { metric: "Conversion Rate", value: "6.2%", change: "+1.5%", trend: "up" },
  ];

  if (!operator) return null;

  // Determine subscription status
  const isPremium = operator.isPremium === true;
  const isFree = !isPremium;

  return (
    <>
      <Helmet>
        <title>Operator Dashboard | SaunaDip</title>
        <meta name="description" content="Manage your sauna and plunge services on SaunaDip" />
      </Helmet>
      
      <div className="bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <h1 className="text-4xl font-bold text-gray-900">
              Operator Dashboard
            </h1>
            
            <Button asChild variant="outline" className="border-sauna-blue text-sauna-blue">
              <Link to="/operator-onboarding">
                <Info size={18} className="mr-2" />
                First Launch Guide
              </Link>
            </Button>
          </div>
          
          {isFree && (
            <div className="bg-sauna-orange/10 border border-sauna-orange/30 rounded-lg p-4 mb-8">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <p className="font-semibold text-sauna-navy">
                    You're on the Free plan
                  </p>
                  <p className="text-sm text-sauna-navy/80">
                    Upgrade to Premium to unlock more features and maximize your visibility.
                  </p>
                </div>
                <Button className="bg-sauna-orange hover:bg-sauna-orange/90">
                  View Pricing Plans
                </Button>
              </div>
            </div>
          )}
          
          <div className="grid gap-8">
            {/* Dashboard Component */}
            <OperatorDashboard 
              isPremium={isPremium}
            />
            
            {/* Business Profile & Photos */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <OperatorProfile 
                  isPremium={isPremium}
                />
              </div>
              <div>
                <PhotoGallery 
                  isPremium={isPremium}
                />
              </div>
            </div>
            
            {/* Service Management & Tags */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <ServiceManagement />
              </div>
              <div>
                <TagsCategories 
                  isPremium={isPremium}
                />
              </div>
            </div>
            
            {/* Stats Table */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle>Insights & Stats</CardTitle>
                  {isFree ? (
                    <Button size="sm" className="bg-sauna-blue">Upgrade for Analytics</Button>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent>
                {!isFree ? (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Metric</TableHead>
                          <TableHead className="text-right">Value</TableHead>
                          <TableHead className="text-right">Change</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {statsData.map((row, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{row.metric}</TableCell>
                            <TableCell className="text-right">{row.value}</TableCell>
                            <TableCell className={`text-right ${
                              row.trend === 'up' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {row.change}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                ) : (
                  <div className="py-12 text-center">
                    <p className="text-gray-500 mb-4">Analytics are available on Premium plan</p>
                    <Button className="bg-sauna-blue">Upgrade Now</Button>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Analytics Charts */}
            <OperatorAnalytics 
              isPremium={isPremium}
            />
            
            {/* Plan Display & Support Messages */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <PlanDisplay 
                isPremium={isPremium}
              />
              <SupportMessages 
                isPremium={isPremium}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OperatorAdmin;
